import { useState, useRef } from 'react';
import { X, Upload, Download, FileText, CheckCircle, AlertCircle, Loader2, RefreshCw, Youtube, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { fetchInfluencer, getSavedTrackers, saveTracker } from '@/utils/instagramTrackerApi';
import { calculateTier, CATEGORIES } from '@/utils/influencerUtils';

const ImportModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | uploading | syncing-ig | syncing-yt | resolving-videos | done
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.name.endsWith('.csv') || f.name.endsWith('.xlsx')) {
      setFile(f);
      setResult(null);
    } else {
      toast.error('Please select a CSV or Excel file');
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/influencer_template.csv?v=3';
    link.download = 'influencer_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setImporting(true);
      setPhase('uploading');

      const res = await api.importInfluencers(file);
      const data = res.data;
      const importedItems = data.imported_items || [];
      const trackedUrlsMap = data.tracked_urls_map || {};

      let igSynced = 0, ytSynced = 0, videosSaved = 0;

      // Phase 1: Sync Instagram metrics
      const igItems = importedItems.filter(item => {
        const platform = item.platform || 'instagram';
        return (platform === 'instagram' || platform === 'both') && item.instagram_handle;
      });

      if (igItems.length > 0) {
        setPhase('syncing-ig');
        setProgress({ current: 0, total: igItems.length, label: 'Syncing Instagram profiles' });
        for (let i = 0; i < igItems.length; i++) {
          setProgress(p => ({ ...p, current: i + 1 }));
          const item = igItems[i];
          const handle = item.instagram_handle.replace('@', '').trim();
          try {
            const liveData = await fetchInfluencer(handle);
            const followers = liveData.profile.followers || 0;
            const er = parseFloat(liveData.computed.engagementRate) || 0;
            const category = liveData.computed.primaryCategory?.toLowerCase() || 'lifestyle';
            await api.updateInfluencer(item.id, {
              name: liveData.profile.fullName || item.name,
              followers_count: followers,
              engagement_rate: er,
              tier: calculateTier(followers),
              category: CATEGORIES.includes(category) ? category : 'lifestyle',
              instagram_profile_pic: liveData.profile.profilePicUrl || '',
              metrics_last_synced: new Date().toISOString(),
            });
            igSynced++;
          } catch (err) {
            console.error(`Auto-sync failed for @${handle}:`, err);
          }
          if (i < igItems.length - 1) await new Promise(r => setTimeout(r, 700));
        }
      }

      // Phase 2: Sync YouTube metrics
      const ytItems = importedItems.filter(item => {
        const platform = item.platform || 'instagram';
        return (platform === 'youtube' || platform === 'both') && item.youtube_handle;
      });

      if (ytItems.length > 0) {
        setPhase('syncing-yt');
        setProgress({ current: 0, total: ytItems.length, label: 'Syncing YouTube channels' });
        for (let i = 0; i < ytItems.length; i++) {
          setProgress(p => ({ ...p, current: i + 1 }));
          const item = ytItems[i];
          try {
            await api.syncYouTubeMetrics(item.id);
            ytSynced++;
          } catch (err) {
            console.error(`YouTube sync failed for ${item.youtube_handle}:`, err);
          }
          if (i < ytItems.length - 1) await new Promise(r => setTimeout(r, 500));
        }
      }

      // Phase 3: Resolve tracked video/reel URLs
      const urlKeys = Object.keys(trackedUrlsMap);
      if (urlKeys.length > 0) {
        setPhase('resolving-videos');
        const allUrlTasks = [];
        for (const item of importedItems) {
          const key = item.instagram_handle || item.youtube_handle || item.name;
          const urls = trackedUrlsMap[key];
          if (urls) allUrlTasks.push({ item, urls });
        }

        setProgress({ current: 0, total: allUrlTasks.length, label: 'Saving tracked videos' });

        for (let i = 0; i < allUrlTasks.length; i++) {
          setProgress(p => ({ ...p, current: i + 1 }));
          const { item, urls } = allUrlTasks[i];

          // Fetch & save YouTube video URLs
          if (urls.yt?.length) {
            const resolved = [];
            for (const url of urls.yt) {
              try {
                const vRes = await api.fetchYouTubeVideo(url);
                resolved.push(vRes.data);
              } catch (e) { /* skip */ }
            }
            if (resolved.length) {
              await api.updateYouTubeTrackedVideos(item.id, resolved);
              videosSaved += resolved.length;
            }
          }

          // Save Instagram reel URLs as Campaign Ad stubs (metrics sync when tracker is opened)
          if (urls.ig?.length) {
            const handle = (item.instagram_handle || '').replace('@', '').toLowerCase();
            if (handle) {
              try {
                const trackers = await getSavedTrackers();
                const currentData = trackers[handle] || { profile: {}, posts: [], computed: {}, trackedAds: [] };
                const existingAds = currentData.trackedAds || [];
                const newAds = [...existingAds];

                for (const url of urls.ig) {
                  // Extract shortcode from URL
                  const parts = url.split('/').filter(p => p !== '');
                  const pidx = parts.indexOf('p') > -1 ? parts.indexOf('p') : parts.indexOf('reel');
                  const shortcode = pidx > -1 && parts[pidx + 1] ? parts[pidx + 1].split('?')[0] : url;

                  if (!newAds.some(a => a.id === shortcode || a.url?.includes(shortcode))) {
                    newAds.push({ id: shortcode, url, tracked_at: new Date().toISOString() });
                    videosSaved++;
                  }
                }

                if (newAds.length > existingAds.length) {
                  await saveTracker(handle, { ...currentData, trackedAds: newAds });
                }
              } catch (e) { /* skip tracker errors */ }
            }
          }
        }
      }

      setResult({ ...data, igSynced, ytSynced, videosSaved });
      setPhase('done');

    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import influencers');
      setPhase('idle');
    } finally {
      setImporting(false);
    }
  };

  const phaseLabel = {
    uploading: 'Uploading & creating profiles…',
    'syncing-ig': 'Fetching Instagram metrics…',
    'syncing-yt': 'Fetching YouTube channel data…',
    'resolving-videos': 'Saving tracked campaign videos…',
  };

  const phaseIcon = {
    uploading: <Upload className="w-6 h-6 text-salty-purple animate-pulse" />,
    'syncing-ig': <Instagram className="w-6 h-6 text-pink-400 animate-pulse" />,
    'syncing-yt': <Youtube className="w-6 h-6 text-red-400 animate-pulse" />,
    'resolving-videos': <RefreshCw className="w-6 h-6 text-amber-400 animate-spin" />,
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-salty-card border border-salty-border rounded-xl w-full max-w-lg m-4 overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-salty-border">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-salty-purple" />
            <h2 className="text-xl font-semibold text-salty-text-main">Import Influencers</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-salty-text-muted hover:text-salty-text-main" disabled={importing}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {importing ? (
            /* Progress view */
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="mb-4">{phaseIcon[phase]}</div>
              <h3 className="text-base font-bold text-salty-text-main mb-1">{phaseLabel[phase]}</h3>
              {progress.total > 0 && (
                <>
                  <p className="text-xs text-salty-text-muted mb-4">{progress.label}</p>
                  <div className="w-full bg-salty-bg h-2 rounded-full overflow-hidden mb-2">
                    <div
                      className="bg-salty-purple h-full transition-all duration-500"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-salty-text-muted uppercase font-semibold">
                    {progress.current} of {progress.total}
                  </span>
                </>
              )}
            </div>
          ) : phase === 'done' && result ? (
            /* Result view */
            <div className="text-center py-4">
              <div className="p-4 rounded-full inline-flex mb-4 bg-emerald-500/10">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold text-salty-text-main mb-2">Import Complete</h3>
              <p className="text-sm text-salty-text-muted mb-6">
                Profiles created and metrics synced from Instagram &amp; YouTube.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-4 bg-salty-bg/50 border border-salty-border rounded-xl">
                  <span className="text-2xl font-bold text-emerald-400 block">{result.imported || 0}</span>
                  <span className="text-[10px] text-salty-text-muted uppercase tracking-wider font-bold">New Influencers</span>
                </div>
                <div className="p-4 bg-salty-bg/50 border border-salty-border rounded-xl">
                  <span className="text-2xl font-bold text-blue-400 block">{result.updated || 0}</span>
                  <span className="text-[10px] text-salty-text-muted uppercase tracking-wider font-bold">Updated Existing</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-salty-bg/50 border border-salty-border rounded-xl">
                  <span className="text-lg font-bold text-pink-400 block">{result.igSynced || 0}</span>
                  <span className="text-[10px] text-salty-text-muted uppercase tracking-wider font-bold">IG Synced</span>
                </div>
                <div className="p-3 bg-salty-bg/50 border border-salty-border rounded-xl">
                  <span className="text-lg font-bold text-red-400 block">{result.ytSynced || 0}</span>
                  <span className="text-[10px] text-salty-text-muted uppercase tracking-wider font-bold">YT Synced</span>
                </div>
                <div className="p-3 bg-salty-bg/50 border border-salty-border rounded-xl">
                  <span className="text-lg font-bold text-blue-400 block">{result.videosSaved || 0}</span>
                  <span className="text-[10px] text-salty-text-muted uppercase tracking-wider font-bold">Ads / Videos Saved</span>
                </div>
              </div>
              <div className="mt-8">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11" onClick={() => { onClose(); if (onSuccess) onSuccess(); }}>
                  Back to Network
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: Template */}
              <div className="p-4 bg-salty-bg/50 border border-salty-border rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-salty-purple/10 rounded-lg shrink-0">
                    <Download className="w-5 h-5 text-salty-purple" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-salty-text-main mb-1">Step 1: Get the template</h3>
                    <p className="text-xs text-salty-text-muted mb-1">
                      Supports <strong>Instagram</strong>, <strong>YouTube</strong>, and <strong>Both</strong> platforms.
                      Add pipe-separated video URLs in <code className="text-[10px] bg-salty-bg px-1 rounded">Instagram_Reel_URLs</code> and <code className="text-[10px] bg-salty-bg px-1 rounded">YouTube_Video_URLs</code> columns to auto-track campaign content.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadTemplate}
                      className="border-salty-purple/30 text-salty-purple hover:bg-salty-purple/10 mt-2"
                    >
                      Download Template.csv
                    </Button>
                  </div>
                </div>
              </div>

              {/* Step 2: Upload */}
              <div>
                <h3 className="text-sm font-semibold text-salty-text-main mb-2 ml-1">Step 2: Upload your file</h3>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all ${file ? 'border-salty-purple bg-salty-purple/5' : 'border-salty-border hover:border-salty-text-muted hover:bg-white/5'}`}
                >
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx" className="hidden" />
                  {file ? (
                    <div className="flex flex-col items-center">
                      <div className="p-3 bg-salty-purple/20 rounded-full mb-3">
                        <FileText className="w-8 h-8 text-salty-purple" />
                      </div>
                      <span className="text-sm text-salty-text-main font-medium">{file.name}</span>
                      <span className="text-[10px] text-salty-text-muted mt-1">{(file.size / 1024).toFixed(1)} KB</span>
                      <Button variant="ghost" size="sm" className="mt-3 text-salty-text-muted hover:text-rose-400 hover:bg-rose-500/10" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                        Remove file
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-salty-bg/50 rounded-full mb-3">
                        <Upload className="w-8 h-8 text-salty-text-muted" />
                      </div>
                      <span className="text-sm text-salty-text-muted font-medium text-center">Click or drag file here to upload</span>
                      <span className="text-[10px] text-salty-text-muted/60 mt-2">Supports .csv and .xlsx</span>
                    </>
                  )}
                </div>
              </div>

              {/* Platform support note */}
              <div className="flex items-center gap-4 px-1">
                <div className="flex items-center gap-1.5 text-xs text-salty-text-muted">
                  <div className="w-2 h-2 rounded-full bg-pink-400" /> Instagram metrics auto-synced
                </div>
                <div className="flex items-center gap-1.5 text-xs text-salty-text-muted">
                  <div className="w-2 h-2 rounded-full bg-red-400" /> YouTube channels auto-synced
                </div>
                <div className="flex items-center gap-1.5 text-xs text-salty-text-muted">
                  <div className="w-2 h-2 rounded-full bg-blue-400" /> Campaign videos saved
                </div>
              </div>

              <Button className="w-full bg-salty-purple hover:bg-salty-purple-light disabled:opacity-50 h-11" disabled={!file} onClick={handleUpload}>
                Start Import
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
